import { PortalTextSubmissionDetails } from '../../Models/Api/strongbox.financialportal';
import { assertNever } from '../../Utils/AssertNever';
import { ApplicationState } from '../index';
import { GetPortalText } from '../PortalAssets';

import {
    FieldGuideCompanySelection,
    FieldGuideConnectionState,
    FieldGuideEngagementSelection,
    FieldGuideUploadState,
} from './Actions';
import { RequestMappings, defaultState } from './Reducer';

/**
 * The signed-in user's current FieldGuide connection state.
 */
export function GetFieldGuideConnectionState(state: ApplicationState): FieldGuideConnectionState {
    return (state && state.fieldGuide) ? state.fieldGuide.connectionState : defaultState.connectionState;
}

/**
 * The connected FieldGuide account's email, when known. Undefined unless the connection is Connected
 * and the connect response carried a fieldGuideEmail.
 */
export function GetFieldGuideEmail(state: ApplicationState): string | undefined {
    return (state && state.fieldGuide) ? state.fieldGuide.fieldGuideEmail : undefined;
}

/**
 * The connected FieldGuide account's display name, when known.
 */
export function GetFieldGuideDisplayName(state: ApplicationState): string | undefined {
    return (state && state.fieldGuide) ? state.fieldGuide.fieldGuideDisplayName : undefined;
}

/**
 * The server's reason the connection needs replacing. Undefined unless ReconnectRequired with a reason.
 */
export function GetFieldGuideReconnectReason(state: ApplicationState): string | undefined {
    return (state && state.fieldGuide) ? state.fieldGuide.reconnectReason : undefined;
}

/**
 * The banner message explaining why the user must reconnect; undefined unless ReconnectRequired. A missing
 * or unrecognised reason falls back to the expired message.
 */
export function GetFieldGuideReconnectMessage(state: ApplicationState): string | undefined {
    if (GetFieldGuideConnectionState(state) !== FieldGuideConnectionState.ReconnectRequired) {
        return undefined;
    }

    const text = GetPortalText<PortalTextSubmissionDetails>(state, 'submissionDetails');

    switch (GetFieldGuideReconnectReason(state)) {
        case 'insufficient_scope':
            return text?.fieldGuideReconnectInsufficientScope
                || 'Your FieldGuide connection is missing required permissions - please reconnect and grant access.';
        case 'account_mismatch':
            return text?.fieldGuideReconnectAccountMismatch
                || 'Your FieldGuide connection belongs to a different account - please reconnect.';
        default:
            return text?.fieldGuideReconnectExpired
                || 'Your FieldGuide credentials have expired - please reconnect.';
    }
}

/**
 * The wizard header's account label: "Connected as {email}" when the email is known, "Connected to
 * FieldGuide" when Connected but the email is unknown, or undefined (no label) for ReconnectRequired
 * or NotConnected.
 */
export function GetFieldGuideHeaderAccountLabel(state: ApplicationState): string | undefined {
    if (GetFieldGuideConnectionState(state) !== FieldGuideConnectionState.Connected) {
        return undefined;
    }

    const email = GetFieldGuideEmail(state);
    return email ? `Connected as ${email}` : 'Connected to FieldGuide';
}


export function GetFieldGuideUploadState(state: ApplicationState): FieldGuideUploadState | undefined {
    return (state && state.fieldGuide) ? state.fieldGuide.uploadState : undefined;
}

export function GetFieldGuideSelectedCompany(state: ApplicationState): FieldGuideCompanySelection | undefined {
    return (state && state.fieldGuide) ? state.fieldGuide.selectedCompany : undefined;
}

export function GetFieldGuideSelectedEngagement(state: ApplicationState): FieldGuideEngagementSelection | undefined {
    return (state && state.fieldGuide) ? state.fieldGuide.selectedEngagement : undefined;
}

export function GetFieldGuideRequestMappings(state: ApplicationState): RequestMappings {
    return (state && state.fieldGuide && state.fieldGuide.requestMappings) ? state.fieldGuide.requestMappings : {};
}

/**
 * Whether the current upload step has what it needs to move on: a company when selecting the company,
 * an engagement when selecting the engagement, and always when mapping requests.
 */
export function CanAdvanceFieldGuideUploadStep(state: ApplicationState): boolean {
    const uploadState = GetFieldGuideUploadState(state);
    switch (uploadState) {
        case FieldGuideUploadState.SelectingCompany:
            return GetFieldGuideSelectedCompany(state) !== undefined;
        case FieldGuideUploadState.SelectingEngagement:
            return GetFieldGuideSelectedEngagement(state) !== undefined;
        case FieldGuideUploadState.MappingRequests:
            return true;
        case undefined:
        case FieldGuideUploadState.Reviewing:
        case FieldGuideUploadState.Sending:
        case FieldGuideUploadState.Succeeded:
        case FieldGuideUploadState.Failed:
            return false;
        default:
            return assertNever(uploadState);
    }
}
